import React, { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'light' | 'dark' | 'oksolar-light' | 'oksolar-dark'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme') as Theme
      if (stored === 'dark' || stored === 'light' || stored === 'oksolar-light' || stored === 'oksolar-dark') {
        return stored
      }
      // Default to system preference
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark'
      }
      return 'light'
    }
    return 'light'
  })

  useEffect(() => {
    const root = window.document.documentElement
    // Remove all theme classes
    root.classList.remove('light', 'dark', 'oksolar-light', 'oksolar-dark')
    // Add the current theme class
    root.classList.add(theme)
    // Also add 'dark' class for Tailwind dark: variant when using any dark theme
    if (theme === 'dark' || theme === 'oksolar-dark') {
      root.classList.add('dark')
    }
    localStorage.setItem('theme', theme)
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
